import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useColor4 } from '../hooks/useColor4';

/**
 * Closing CTA for the Meet The Team page — full-bleed background with the
 * final "Come meet us in person" invitation and a single primary CTA
 * (Contact Us). Button uses the same gradient + layered-shadow + shimmer
 * treatment as the home-page Hero CTA so site-wide CTAs read as one family.
 */

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  // Later gallery index than the page hero so the closing CTA shows a
  // different photo than the banner at the top of the page.
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[6].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[6].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 6,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A warm, welcoming photo of this specific property — a bright common area, lobby, courtyard, or signature exterior that reads "come visit us". Must be different from the Meet The Team page hero banner at the top of the page.',
    context:
      'Full-bleed background for the closing "come meet us" CTA on the Meet The Team page. Must visually differ from the page hero banner.',
  },
};

const primaryLinkDef: SlotGroupDefinition = {
  label: 'Primary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function TeamCTA() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();
  const color4 = useColor4();

  const bg = useSlotGroup('teamcta.bg_image', bgImageDef);
  const primary = useSlotGroup('teamcta.primary_link', primaryLinkDef);

  return (
    <section className="relative w-full overflow-hidden">
      <EditableSlotGroup groupPath="teamcta.bg_image" definition={bgImageDef}>
        <div className="absolute inset-0">
          {bg.image ? (
            <VibesitesImage
              url={bg.image}
              alt={bg.alt || ''}
              sizes="100vw"
              className="w-full h-full object-cover object-center"
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-primary)]" />
          )}
        </div>
      </EditableSlotGroup>

      <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/45" />

      <div
        ref={sectionRef}
        className="reveal-fade-up relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center text-white"
      >
        <RichTextContent
          path="teamcta.title"
          label="Title"
          defaultValue="<p>Come Meet Us In Person</p>"
          as="h2"
          className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal leading-tight tracking-tight max-w-3xl mx-auto drop-shadow"
        />
        <RichTextContent
          path="teamcta.body"
          label="Body"
          defaultValue='<p>Have a question or ready to tour? The team at <span data-type="field-token" data-field="property_name"></span> is here to help you find your next home.</p>'
          as="div"
          className="mt-6 font-body text-base sm:text-lg leading-relaxed text-white/90 max-w-2xl mx-auto [&_p]:mb-0"
        />

        <div className="mt-10 flex items-center justify-center">
          <EditableSlotGroup groupPath="teamcta.primary_link" definition={primaryLinkDef}>
            <a
              href={primary.url || '/contact-us'}
              target={primary._target || '_self'}
              style={color4?.primaryOverride}
              className="team-cta-btn group/cta relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-semibold tracking-[0.2em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{primary.label || 'Contact Us'}</span>
              <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .team-cta-btn {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-radius: 2px;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.2),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .team-cta-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .team-cta-btn:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 4px 12px rgba(0,0,0,0.2),
            0 8px 24px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
          transform: translateY(-2px);
        }
        .team-cta-btn:hover::before {
          left: 100%;
        }
        .team-cta-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'TeamCTA',
  displayName: 'Team CTA',
  tokens: {
    'teamcta.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Come Meet Us In Person</p>',
    },
    'teamcta.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>Have a question or ready to tour? The team at <span data-type="field-token" data-field="property_name"></span> is here to help you find your next home.</p>',
    },
  },
  slotGroups: {
    'teamcta.bg_image': bgImageDef,
    'teamcta.primary_link': primaryLinkDef,
  },
};

(TeamCTA as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TeamCTA;
